import { api } from '@/lib/api'
import type {
  AcceptInvitePayload,
  AcknowledgeInvitationResponse,
  CognitoSessionResponse,
  CognitoSigninResponse,
  ConfirmDeviceResponse,
  InvitationDetails,
  PasswordResetResponse,
  SignupResponse,
  VerifyEmailResponse,
} from './types'

export const authApi = {
  cognitoSignin: (body: { email: string; password: string; deviceKey?: string | null }) =>
    api.post<CognitoSigninResponse>('/api/auth/cognito/signin', body),
  cognitoConfirm: (body: {
    email: string
    session: string
    challengeName: string
    responses: Record<string, string>
  }) => api.post<CognitoSigninResponse>('/api/auth/cognito/confirm', body),
  cognitoSession: (cognito_token: string) =>
    api.post<CognitoSessionResponse>('/api/auth/cognito/session', { cognito_token }),
  cognitoConfirmDevice: (body: {
    accessToken: string
    deviceKey: string
    deviceGroupKey: string
    deviceName: string
  }) => api.post<ConfirmDeviceResponse>('/api/auth/cognito/confirm-device', body),
  legacyLogin: (email: string, password: string) =>
    api.post<CognitoSessionResponse>('/api/auth/login', { email, password }),
  adminCognitoSignin: (body: { email: string; password: string }) =>
    api.post<CognitoSigninResponse>('/api/auth/admin/cognito/signin', body),
  adminCognitoConfirm: (body: {
    email: string
    session: string
    challengeName: string
    responses: Record<string, string>
  }) => api.post<CognitoSigninResponse>('/api/auth/admin/cognito/confirm', body),
  adminCognitoSession: (cognito_token: string) =>
    api.post<CognitoSessionResponse>('/api/auth/admin/cognito/session', { cognito_token }),
  adminLocalSignin: (body: { email: string; password: string }) =>
    api.post<CognitoSigninResponse & CognitoSessionResponse & { localMode?: boolean }>(
      '/api/auth/admin/local/signin',
      body,
    ),
  adminLocalTotp: (body: { session: string; code: string }) =>
    api.post<CognitoSessionResponse & { step?: string; localMode?: boolean }>(
      '/api/auth/admin/local/totp',
      body,
    ),
  signup: (body: { email: string; password: string; name?: string; inviteToken?: string }) =>
    api.post<SignupResponse & {
      token?: string
      userId?: number
      email?: string
      name?: string
      redirect?: string
      tenantId?: string
      roleName?: string
    }>('/api/auth/signup', body),
  resendVerification: (email: string) =>
    api.post<{ message?: string; error?: string }>('/api/auth/resend-verification', { email }),
  verifyEmail: (token: string) =>
    api.get<VerifyEmailResponse>(`/api/auth/verify-email?token=${encodeURIComponent(token)}`),
  forgotPassword: (email: string) =>
    api.post<PasswordResetResponse>('/api/auth/forgot-password', { email }),
  resetPassword: (body: { token: string; password: string }) =>
    api.post<PasswordResetResponse>('/api/auth/reset-password', body),
  getInvitation: (token: string) =>
    api.get<InvitationDetails>(`/api/users/invitations/${encodeURIComponent(token)}`),
  acknowledgeInvitation: (token: string) =>
    api.post<AcknowledgeInvitationResponse>(
      `/api/users/invitations/${encodeURIComponent(token)}/acknowledge`,
      {},
    ),
  acceptInvitation: (payload: AcceptInvitePayload) =>
    api.post<CognitoSessionResponse>(
      `/api/users/invitations/${encodeURIComponent(payload.token)}/accept`,
      { password: payload.password, name: payload.name },
    ),
  logout: () => api.post<{ message?: string }>('/api/auth/logout'),
}

export const DEVICE_STORAGE_KEY = 'bk.cognito.device'
